import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import "../styles/Subscribe.css";
import Header from "./Header";
import Footer from "./Footer";
import {
  getMembershipDuration,
  getMembershipPrice,
  getMembershipTier,
  membershipDurations,
  membershipFeatures,
  membershipTiers,
} from "../utils/constants";
import LocaleLink from "./LocaleLink";
import { useLanguage } from "../context/Language";

// Manual payment is live: the user confirms their account email, we issue a
// reference, and they finish on WhatsApp with a bank transfer. Online checkout
// stays off until a payment provider is signed.
const CHECKOUT_ENABLED = false;
const MANUAL_PAYMENT_ENABLED = true;

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;
const BACKEND = process.env.REACT_APP_BACKEND;

const localize = (value, lang) => value?.[lang] || value?.en || "";
const formatPrice = (value) => `${Math.round(Number(value))} MAD`;

const Subscribe = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [selectedTier, setSelectedTier] = useState("premium");
  const [selectedDuration, setSelectedDuration] = useState("quarterly");

  const [email, setEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [reference, setReference] = useState(null);
  // Immediate access and withdrawal waiver (Terms of Service, section 16). Unticked by default.
  const [immediateAccess, setImmediateAccess] = useState(null); // null until the buyer chooses

  const [copied, setCopied] = useState(false);

  const [openFaq, setOpenFaq] = useState(null);

  const lang = i18n.language?.split("-")[0] || "en";
  const isRTL = lang === "ar";
  const { localePath } = useLanguage();

  // A reference is tied to one plan. If the user picks a different tier or
  // duration, the old reference no longer matches what they are looking at,
  // so we clear it and let them generate a new one.
  useEffect(() => {
    setReference(null);
    setRequestError("");
  }, [selectedTier, selectedDuration]);

  const toggleFaq = (faqId) => {
    setOpenFaq((current) => (current === faqId ? null : faqId));
  };

  const selectedTierData = useMemo(
    () => getMembershipTier(selectedTier),
    [selectedTier],
  );

  const selectedDurationData = useMemo(
    () => getMembershipDuration(selectedDuration),
    [selectedDuration],
  );

  const selectedFeatures = useMemo(
    () =>
      membershipFeatures.filter(
        (feature) => !feature.free && feature[selectedTier],
      ),
    [selectedTier],
  );

  const selectedPrice = getMembershipPrice(selectedTier, selectedDuration);
  const monthlyPrice = getMembershipPrice(selectedTier, "monthly");
  const monthlyEquivalent = selectedPrice / selectedDurationData.months;
  const savings = Math.max(
    0,
    monthlyPrice * selectedDurationData.months - selectedPrice,
  );

  const handleContinue = async () => {
    if (!MANUAL_PAYMENT_ENABLED || requesting) return;

    const cleanEmail = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
      setRequestError(t("Please enter the email of your Mabrook account."));
      return;
    }

    if (immediateAccess === null) {
      setRequestError(t("waiverRequired"));
      return;
    }

    setRequestError("");
    setRequesting(true);

    try {
      const response = await fetch(`${BACKEND}/website/subscription-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: cleanEmail,
          tier: selectedTier,
          durationMonths: selectedDurationData.months,
          immediateAccess: immediateAccess === true,
        }),
      });
      const data = await response.json();

      if (data.status === "201") {
        setReference({ code: data.reference, amount: data.amountMAD, immediate: immediateAccess });
      } else {
        setRequestError(
          data.message?.[lang] ||
            data.message?.en ||
            t("Something went wrong."),
        );
      }
    } catch {
      setRequestError(t("Something went wrong. Please try again."));
    } finally {
      setRequesting(false);
    }
  };

  const whatsappLink = () => {
    const text = [
      t("Hello, I would like to subscribe to Mabrook."),
      "",
      `${t("Reference")}: ${reference.code}`,
      `${t("Plan")}: ${t(selectedTierData.labelKey)} ${localize(selectedDurationData.label, lang)}`,
      `${t("Amount")}: ${reference.amount} MAD`,
      `${t("Account")}: ${email.trim().toLowerCase()}`,
    ].join("\n");
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  return (
    <>
      <Header />

      <main
        className={`subscribe-page subscribe-tier-${selectedTier}`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <section className="subscribe-upgrade">
          <div className="subscribe-decoration subscribe-decoration-one" />
          <div className="subscribe-decoration subscribe-decoration-two" />

          <div className="subscribe-container subscribe-upgrade-grid">
            <div className="subscribe-introduction">
              <span className="subscribe-eyebrow">
                <span aria-hidden="true" />
                {selectedTier == "standard" ? t("Standard") : t("Premium")}
              </span>

              <h1>{t("More ways to find something real.")}</h1>

              <p className="subscribe-lead">
                {t(
                  "Choose Standard or Premium, then select the membership duration that suits you.",
                )}
              </p>

              <div className="subscribe-included-heading">
                <span>
                  {t("Included with")} {t(selectedTierData.labelKey)}
                </span>
                <small>
                  {selectedTier === "premium"
                    ? t("Everything in Standard plus the matching algorithm.")
                    : t("All paid features except the matching algorithm.")}
                </small>
              </div>

              <ul className="subscribe-feature-grid" aria-live="polite">
                {selectedFeatures.map((feature) => (
                  <li
                    key={feature.id}
                    className={
                      feature.premiumExclusive ? "premium-exclusive" : ""
                    }
                  >
                    <span className="subscribe-feature-icon">
                      <FaCheck aria-hidden="true" />
                    </span>

                    <span className="subscribe-feature-copy">
                      <span>{t(feature.labelKey)}</span>
                      {feature.premiumExclusive && (
                        <small>{t("Premium exclusive")}</small>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                className="subscribe-gift-link"
                onClick={() => navigate(localePath("/gift"))}
              >
                <span aria-hidden="true">🎁</span>
                <span>{t("Explore gift memberships")}</span>
                <span aria-hidden="true">→</span>
              </button>
            </div>

            <aside className="subscribe-purchase-panel">
              <div className="subscribe-tier-selector-heading">
                <div>
                  <span>{t("Membership level")}</span>
                  <strong>{t("Choose your experience")}</strong>
                </div>

                <small>{t("Prices shown in MAD")}</small>
              </div>

              <div
                className="subscribe-tier-switcher"
                role="radiogroup"
                aria-label={t("Membership level")}
              >
                {membershipTiers.map((tier) => {
                  const isSelected = selectedTier === tier.id;
                  const startingPrice = getMembershipPrice(tier.id, "monthly");

                  return (
                    <button
                      key={tier.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`${isSelected ? "selected" : ""} tier-${
                        tier.id
                      }`}
                      onClick={() => setSelectedTier(tier.id)}
                    >
                      <span className="subscribe-tier-radio" aria-hidden="true">
                        <span />
                      </span>

                      <span className="subscribe-tier-copy">
                        <strong>Mabrook {t(tier.labelKey)}</strong>
                        <small>{t(tier.descriptionKey)}</small>
                      </span>

                      <span className="subscribe-tier-from">
                        <small>{t("From")}</small>
                        <strong>{formatPrice(startingPrice)}</strong>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="subscribe-panel-header">
                <div>
                  <span>{t("Membership duration")}</span>
                  <strong>Mabrook {t(selectedTierData.labelKey)}</strong>
                </div>

                <span className="subscribe-panel-status">
                  <FaCheck aria-hidden="true" />
                  {selectedFeatures.length} {t("paid features")}
                </span>
              </div>

              <div
                className="subscribe-plan-switcher"
                role="radiogroup"
                aria-label={t("Membership duration")}
              >
                {membershipDurations.map((duration) => {
                  const isSelected = selectedDuration === duration.id;
                  const price = getMembershipPrice(selectedTier, duration.id);
                  const perMonth = price / duration.months;

                  return (
                    <button
                      key={duration.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={isSelected ? "selected" : ""}
                      onClick={() => setSelectedDuration(duration.id)}
                    >
                      <span>{localize(duration.label, lang)}</span>
                      <small>
                        {formatPrice(perMonth)} / {t("month")}
                      </small>

                      {duration.badgeKey && <em>{t(duration.badgeKey)}</em>}
                    </button>
                  );
                })}
              </div>

              <div className="subscribe-price-area" aria-live="polite">
                <span className="subscribe-price-label">
                  {t("Your selected membership")}
                </span>

                <div className="subscribe-price-line">
                  <strong>{formatPrice(selectedPrice)}</strong>
                </div>

                <p>
                  Mabrook {t(selectedTierData.labelKey)} ·{" "}
                  {localize(selectedDurationData.label, lang)}
                </p>

                <div className="subscribe-price-meta">
                  <span>
                    {formatPrice(monthlyEquivalent)} / {t("month")}
                  </span>

                  {savings > 0.005 && (
                    <strong>
                      {t("You save")} {formatPrice(savings)}
                    </strong>
                  )}
                </div>
              </div>

              <div className="subscribe-divider" />

              <div className="subscribe-panel-assurance">
                <FaCheck aria-hidden="true" />
                <span>{t("One-time payment. No automatic renewal.")}</span>
              </div>

              <p className="subscribe-legal">
                {t(
                  "A single payment for a fixed membership period. There is no automatic renewal. When the period ends, your next scheduled membership begins automatically if you have one; otherwise your account returns to the Free tier.",
                )}{" "}
                <LocaleLink to="/terms-of-service">
                  {t("Terms of Service")}
                </LocaleLink>
                <span aria-hidden="true"> · </span>
                <LocaleLink to="/refund-policy">
                  {t("Refund Policy")}
                </LocaleLink>
                <span aria-hidden="true"> · </span>
                <LocaleLink to="/privacy-policy">
                  {t("Privacy Policy")}
                </LocaleLink>
              </p>

              {reference ? (
                <div className="subscribe-reference-box">
                  <p className="subscribe-reference-label">
                    {t("Your reference")}
                  </p>
                  <div className="subscribe-reference-row">
                    <div className="subscribe-reference-field">
                      <span className="subscribe-reference-code">
                        {reference.code}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="subscribe-copy-button"
                      onClick={() => {
                        navigator.clipboard?.writeText(reference.code);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                    >
                      {copied ? t("Copied") : t("Copy")}
                    </button>
                  </div>
                  <p className="subscribe-reference-note">
                    {reference.immediate ? t("waiverChosen") : t("waiverDeclined")}
                  </p>
                  <p className="subscribe-reference-note">
                    {t(
                      "Send us this reference on WhatsApp. We will reply with the bank details and add the membership to your Mabrook account once the transfer is confirmed.",
                    )}
                  </p>
                  <a
                    className="subscribe-whatsapp-button"
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Continue on WhatsApp")}
                  </a>
                </div>
              ) : (
                <>
                  <label
                    className="subscribe-email-label"
                    htmlFor="subscribe-email"
                  >
                    {t("Email of your Mabrook account")}
                  </label>
                  <input
                    id="subscribe-email"
                    type="email"
                    className="subscribe-email-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (requestError) setRequestError("");
                    }}
                    placeholder={t("youremail@example.com")}
                    autoComplete="email"
                  />
                  <p className="subscribe-email-hint">
                    {t(
                      "You need a Mabrook account first. Create one in the app, then come back here.",
                    )}
                  </p>

                  <fieldset style={{ border: "none", padding: 0, margin: "16px 0", textAlign: "start" }}>
                    <legend style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>{t("waiverQuestion")}</legend>
                    {[
                      { value: true, label: t("waiverOptionNow") },
                      { value: false, label: t("waiverOptionLater") },
                    ].map((opt) => (
                      <label
                        key={String(opt.value)}
                        style={{ display: "flex", gap: 10, alignItems: "flex-start", margin: "8px 0", fontSize: 14, lineHeight: 1.45, cursor: "pointer" }}
                      >
                        <input
                          type="radio"
                          name="immediateAccess"
                          checked={immediateAccess === opt.value}
                          onChange={() => {
                            setImmediateAccess(opt.value);
                            setRequestError("");
                          }}
                          style={{ marginTop: 3, flexShrink: 0 }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </fieldset>

                  {requestError && (
                    <p className="subscribe-error">{requestError}</p>
                  )}

                  <button
                    type="button"
                    className="subscribe-checkout-button"
                    onClick={handleContinue}
                    disabled={requesting}
                  >
                    <span>
                      {requesting ? t("Please wait...") : t("Continue")}
                    </span>
                    <span aria-hidden="true">→</span>
                  </button>
                </>
              )}
            </aside>
          </div>
        </section>
        <section className="subscribe-faq-section">
          <div className="subscribe-container subscribe-faq-layout">
            <div className="subscribe-faq-introduction">
              <span className="subscribe-faq-eyebrow">
                {t("Membership questions")}
              </span>

              <h2>{t("Questions about subscribing")}</h2>

              <p>
                {t(
                  "What to know about starting, extending and scheduling your Mabrook membership.",
                )}
              </p>
            </div>

            <div className="subscribe-faq">
              <details open={openFaq === "start"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("start");
                  }}
                >
                  <span>{t("When does my membership start?")}</span>
                  <span className="subscribe-faq-symbol" aria-hidden="true">
                    {openFaq === "start" ? "−" : "+"}
                  </span>
                </summary>

                <p>
                  {t(
                    "Your Mabrook membership starts once your payment is confirmed. If you already have membership time remaining, your new membership is added after it so you do not lose any time.",
                  )}
                </p>
              </details>

              <details open={openFaq === "existing"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("existing");
                  }}
                >
                  <span>
                    {t("What if I already have a Mabrook membership?")}
                  </span>
                  <span className="subscribe-faq-symbol" aria-hidden="true">
                    {openFaq === "existing" ? "−" : "+"}
                  </span>
                </summary>

                <p>
                  {t(
                    "You can still subscribe. Your new membership will either extend your current membership or be scheduled after the membership already on your account. Existing paid membership time is never replaced.",
                  )}
                </p>
              </details>

              <details open={openFaq === "different-level"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("different-level");
                  }}
                >
                  <span>
                    {t(
                      "Can I choose a different membership level from the one I currently have?",
                    )}
                  </span>

                  <span className="subscribe-faq-symbol" aria-hidden="true">
                    {openFaq === "different-level" ? "−" : "+"}
                  </span>
                </summary>

                <p>
                  {t(
                    "Yes. Your current membership remains unchanged until it ends. The new membership level will then start automatically after it.",
                  )}
                </p>
              </details>

              <details open={openFaq === "renewal"}>
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    toggleFaq("renewal");
                  }}
                >
                  <span>
                    {t("Does a website membership renew automatically?")}
                  </span>
                  <span className="subscribe-faq-symbol" aria-hidden="true">
                    {openFaq === "renewal" ? "−" : "+"}
                  </span>
                </summary>

                <p>
                  {t(
                    "No. Website memberships are one-time payments for the duration you choose. Mabrook will not automatically charge you again.",
                  )}
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Subscribe;
